// A jellyfish in a water tank, in toy space. It swims the way a real one does:
// the bell contracts quickly, pushing a jet of water out behind it, then
// relaxes slowly while it glides; water drag slows it and it sinks a little
// between pulses. Left alone it pulses now and then to hold its depth. A poke
// makes it contract hard and turn to jet away from the finger. Tentacles
// hang from the bell's rim as chains of points that keep their segment
// length and trail through the water.
const v3=(a,b,c)=>[a,b,c],len=v=>Math.hypot(v[0],v[1],v[2]);
const norm=v=>{const l=len(v)||1;return [v[0]/l,v[1]/l,v[2]/l];};
export class JellySwim{
  constructor({bounds,start,size,anchors,segments=10,length,random=Math.random}){
    this.bounds={min:[...bounds.min],max:[...bounds.max]};this.size=size;this.random=random;this.anchors=anchors.map(a=>[...a]);
    this.segments=segments;this.segment=length/segments;this.start=[...start];this.reset();
  }
  reset(){
    this.p=[...this.start];this.v=[0,0,0];this.axis=[0,1,0];this.pulse=null;this.nextPulse=.8+this.random();this.contraction=0;this.clock=0;
    this.tentacles=this.anchors.map(a=>{const base=this.anchor(a);return Array.from({length:this.segments+1},(_,k)=>[base[0],base[1]-k*this.segment,base[2]]);});
    this.previous=this.tentacles.map(t=>t.map(q=>[...q]));
  }
  // A rim point in the tank: rotated so the bell's top points along axis,
  // pulled in a little while the bell is contracted.
  anchor(a){
    const up=this.axis,ref=Math.abs(up[1])<.99?[0,1,0]:[1,0,0];
    let x=norm([ref[1]*up[2]-ref[2]*up[1],ref[2]*up[0]-ref[0]*up[2],ref[0]*up[1]-ref[1]*up[0]]);
    if(Math.abs(up[1])>.99)x=[1,0,0];
    const z=[x[1]*up[2]-x[2]*up[1],x[2]*up[0]-x[0]*up[2],x[0]*up[1]-x[1]*up[0]],squeeze=1-.3*this.contraction;
    // For an upright bell, x stays the toy's x and z its z.
    const ax=up[1]>.99?[1,0,0]:x,az=up[1]>.99?[0,0,1]:z;
    return [0,1,2].map(i=>this.p[i]+(ax[i]*a[0]+az[i]*a[2])*squeeze+up[i]*a[1]);
  }
  startPulse(strength){this.pulse={t:0,strength,pushed:false};}
  // Poke from a point: turn away from it and jet off.
  poke(point){
    const away=norm([this.p[0]-point[0],this.p[1]-point[1]+this.size*.6,this.p[2]-point[2]]);
    this.axis=norm(this.axis.map((v,i)=>v*.3+away[i]*.7));this.turnTo=away;this.startPulse(2.6);this.nextPulse=this.clock+2.5;
    return true;
  }
  step(dt){
    const events=[],R=this.size;this.clock+=dt;
    if(!this.pulse&&this.clock>=this.nextPulse){this.startPulse(1);this.nextPulse=this.clock+1.8+1.4*this.random();this.turnTo=null;events.push('pulse');}
    // Contraction: quick squeeze, slow relax.
    if(this.pulse){
      const s=this.pulse;s.t+=dt;
      this.contraction=s.t<.22?Math.sin(s.t/.22*Math.PI/2):Math.max(0,1-(s.t-.22)/.65);
      if(!s.pushed&&s.t>=.12){s.pushed=true;for(let i=0;i<3;i++)this.v[i]+=this.axis[i]*R*2.1*s.strength;events.push('jet');}
      if(s.t>.9)this.pulse=null;
    }else this.contraction=0;
    // Water: a slight sink and drag.
    this.v[1]-=R*.55*dt;const drag=Math.exp(-1.7*dt);for(let i=0;i<3;i++){this.v[i]*=drag;this.p[i]+=this.v[i]*dt;}
    const {min,max}=this.bounds,pad=[R*1.05,R*.8,R*1.05];
    for(let i=0;i<3;i++){
      if(this.p[i]<min[i]+pad[i]){this.p[i]=min[i]+pad[i];this.v[i]=Math.abs(this.v[i])*.3;}
      if(this.p[i]>max[i]-pad[i]){this.p[i]=max[i]-pad[i];this.v[i]=-Math.abs(this.v[i])*.3;}
    }
    // Low in the tank it aims upward; after a poke it keeps its new heading a moment.
    const speed=len(this.v),want=this.turnTo&&this.pulse?this.turnTo:norm([this.v[0]*.4,1+(this.p[1]<(min[1]+max[1])*.45?.6:0),this.v[2]*.4]);
    const k=Math.min(1,dt*(speed>R?2.5:1.2));this.axis=norm(this.axis.map((v,i)=>v+(want[i]-v)*k));
    // Tentacles: Verlet points with water drag, held at the rim.
    const g=R*1.2;
    this.tentacles.forEach((t,ti)=>{
      const prev=this.previous[ti],base=this.anchor(this.anchors[ti]);
      for(let k=1;k<t.length;k++){
        const q=t[k],o=prev[k],next=[q[0]+(q[0]-o[0])*.9,q[1]+(q[1]-o[1])*.9-g*dt*dt,q[2]+(q[2]-o[2])*.9];
        prev[k]=[...q];t[k]=next;
      }
      t[0]=base;prev[0]=[...base];
      for(let iter=0;iter<4;iter++)for(let k=1;k<t.length;k++){
        const a=t[k-1],b=t[k],d=[b[0]-a[0],b[1]-a[1],b[2]-a[2]],l=len(d)||1e-6,fix=(l-this.segment)/l;
        if(k===1){for(let i=0;i<3;i++)b[i]-=d[i]*fix;}
        else{for(let i=0;i<3;i++){a[i]+=d[i]*fix*.5;b[i]-=d[i]*fix*.5;}}
      }
      for(const q of t)for(let i=0;i<3;i++)q[i]=Math.max(min[i],Math.min(max[i],q[i]));
    });
    return events;
  }
}
